import React, { useEffect, useRef, useState } from "react";
import { DatePicker as MaterialDatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePickerSlotsComponentsProps } from "@mui/x-date-pickers";
import createPalette from "@mui/material/styles/createPalette";
import { CssBaseline, ThemeProvider, createTheme } from "@mui/material";

import { vars } from "../Variables";
import dayjs, { Dayjs } from "dayjs";
import { isEqual } from "lodash";
import { useField } from "formik";

const { primaryFont, primaryColor, errorColor, greyLight, grey, greyDark } =
  vars;

export const views = {
  day: "day",
  month: "month",
  year: "year",
} as const;

type viewKeys = keyof typeof views;

interface IDatepicker {
  label?: string;
  view: (typeof views)[viewKeys];
  sx?: Record<string, any>;
  slotProps?: DatePickerSlotsComponentsProps<unknown>;
  name?: string;
  value: string;
  onChange?: (value: string) => void;
  variant?: "outlined" | "standard";
}

const customPalette = createPalette({
  primary: {
    main: primaryColor,
  },
  grey: {
    "100": greyLight,
    "200": grey,
    "300": greyDark,
  },
  error: {
    main: errorColor,
  },
});

const theme = createTheme({
  typography: {
    fontFamily: primaryFont,
  },
  components: {
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          fontWeight: 400,
          fontSize: "12px",
          lineHeight: "183%",
          color: customPalette.grey[300],
        },
      },
    },
    MuiInput: {
      styleOverrides: {
        input: {},
        root: {
          "&:hover:not(.Mui-disabled, .Mui-error)::before": {
            borderColor: customPalette.grey[200],
            borderWidth: 1,
          },

          "&:after": {
            borderColor: customPalette.primary.main,
          },
          "&:before": {
            borderColor: customPalette.grey[200],
          },
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        asterisk: {
          color: customPalette.error.main,
        },
        root: {
          fontWeight: 400,
          fontSize: "1rem",
          color: customPalette?.grey[100],

          "&.Mui-focused": {
            color: customPalette?.grey[100],
          },
        },
      },
    },
    MuiCssBaseline: {
      styleOverrides: (themeParam) => ({
        ".MuiPickersDay-root.Mui-selected": {
          backgroundColor: `${customPalette.primary.main} !important`,
        },
      }),
    },
  },
});

const classes = {
  root: {
    width: "100%",
  },
};

const formatByViews = {
  day: "DD-MM-YYYY",
  month: "MMM-YYYY",
  year: "YYYY",
};

export const Datepicker = ({
  sx,
  view = "day",
  value = "",
  onChange,
  variant = "standard",
  name = "date",
  ...props
}: IDatepicker) => {
  const [field, , helpers] = useField({ name });
  const [isOpen, setIsOpen] = useState(false);

  // const [dateValue, setDateValue] = useState<string | Dayjs>(value);

  const externalValueRef = useRef<string | Dayjs>("");

  useEffect(() => {
    // Only set the external value if it's different
    if (!isEqual(externalValueRef.current, value)) {
      helpers.setValue(value);
    }
    externalValueRef.current = value;
  }, [value]);

  // Only allow valid views in the field.
  view = Object.keys(views).includes(view) ? view : "day";
  // Get format based on view
  const format = formatByViews[view];

  const onDateChange = (v: any) => {
    onChange?.(dayjs(v).format(format));
    helpers.setValue(v);
  };

  // Format value as per the view of the field
  const formattedValue =
    field.value &&
    dayjs(dayjs(field.value, formatByViews.day).format(format), format);

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <MaterialDatePicker
          {...props}
          sx={{ ...classes.root, sx }}
          open={isOpen}
          {...(formattedValue ? { value: formattedValue } : {})}
          onClose={() => setIsOpen(false)}
          onChange={onDateChange}
          format={format}
          slotProps={{
            textField: {
              variant,
              onClick: () => setIsOpen(true),
              name: field.name,
            },
          }}
          views={[Object.keys(views).includes(view) ? view : "day"]}
        />
      </ThemeProvider>
    </LocalizationProvider>
  );
};
